import { Suspense, lazy, useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, Route, Sparkles } from "lucide-react";

const GalaxyScene = lazy(() => import("@/components/galaxy/GalaxyScene"));

const supportsWebgl = (): boolean => {
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2") ?? canvas.getContext("webgl"));
  } catch {
    return false;
  }
};

/** Static deep-space stand-in when WebGL is unavailable or motion is reduced. */
function GalaxyStaticFallback() {
  return (
    <div className="galaxy-fallback" aria-hidden="true">
      <div className="galaxy-fallback-nebula galaxy-fallback-nebula--violet" />
      <div className="galaxy-fallback-nebula galaxy-fallback-nebula--cyan" />
      <svg className="galaxy-fallback-constellation" viewBox="0 0 1200 700">
        <g className="galaxy-fallback-lines">
          <path d="M120 510 275 390 410 470 525 315 690 370 790 210 960 275 1095 155" />
          <path d="M275 390 350 220 525 315 610 145 790 210" />
          <path d="M410 470 610 545 690 370 875 505 960 275" />
        </g>
        <g className="galaxy-fallback-stars">
          {[120, 275, 410, 525, 690, 790, 960, 1095].map((x, index) => (
            <circle
              key={x}
              cx={x}
              cy={[510, 390, 470, 315, 370, 210, 275, 155][index]}
              r={index % 3 === 0 ? 5 : 3}
            />
          ))}
        </g>
      </svg>
      <span className="galaxy-fallback-body galaxy-fallback-body--one" />
      <span className="galaxy-fallback-body galaxy-fallback-body--two" />
      <span className="galaxy-fallback-body galaxy-fallback-body--three" />
    </div>
  );
}

export function HomePage() {
  const canRender3d = useMemo(
    () =>
      supportsWebgl() &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  return (
    <div className="galaxy-page">
      <section className="galaxy-stage" aria-hidden="true">
        {canRender3d ? (
          <Suspense fallback={<GalaxyStaticFallback />}>
            <GalaxyScene />
          </Suspense>
        ) : (
          <GalaxyStaticFallback />
        )}

        <div className="galaxy-hero">
          <div className="galaxy-hero-eyebrow">
            <span /> AIHR · Cosmos / Knowledge constellation
          </div>
          <h1 className="galaxy-hero-title">
            Your learning universe, <em>connected.</em>
          </h1>
          <p className="galaxy-hero-sub">
            Explore the signals across your learning ecosystem. Each celestial
            body is a live data domain — hover to discover, select to ask Cosmos.
          </p>
        </div>

        <div className="galaxy-quick-links">
          <Link
            to="/workspace"
            className="galaxy-quick-link galaxy-quick-link--primary"
          >
            <Sparkles size={15} />
            Open workspace
            <ArrowRight size={14} />
          </Link>
          <Link to="/courses" className="galaxy-quick-link">
            <BookOpen size={15} />
            Course catalog
          </Link>
          <Link to="/journeys/new" className="galaxy-quick-link">
            <Route size={15} />
            Journey commands
          </Link>
        </div>
      </section>
    </div>
  );
}
